import { IconButton } from '../index';

import type { useTheme } from './theme';
import { rowIdOf } from './state/loggerDraft';
import type { GroupMember } from './supersetNotebook';
import { PositionCue } from './PositionCue';
import type { CardHints } from './cardHints';
import type { HoldProps } from './useTransientTip';

// SupersetTabs: the superset control of the logger card. Since #308 it rides
// the chip row, after the clock and before BI (Antenore on v2: «Proviamo a
// chiamarlo S+ e lo metti prima di BI, dopo l'orologio»). Standalone, a link
// icon (#318) that creates or joins a superset. Grouped, a marking CUE (loop glyph +
// "N/M") whose tap cycles to the next member, plus the ☰ superset menu.
// The row wraps, so on a narrow phone the ☰ goes to the next line rather
// than off screen.
//
// State (done/in-progress/not-done) comes from groupMembersOf; the data model
// is untouched.
export function SupersetTabs({
  members,
  activeEntryId,
  groupLabel,
  resolveName,
  onSelect,
  onSplit,
  onCreate,
  onOpenMenu,
  editLocked,
  showTip,
  hold,
  hints,
  t,
  theme,
}: {
  /** Group members (≥1) when grouped; empty for a standalone exercise. */
  members: GroupMember[];
  /** WHICH MEMBER ROW is in focus: DraftEntry.entryId (LG-39). A circuit may
   *  hold the same exercise twice, so the slug cannot say which. */
  activeEntryId: string | null;
  /** Localized group label, e.g. "Superset 1": the cue's spoken label only. */
  groupLabel: string;
  resolveName: (slug: string, fallback: string) => string;
  onSelect: (entryId: string) => void;
  /** Splits/dissolves the group (☰ fallback when onOpenMenu is undefined). */
  onSplit: () => void;
  /** Standalone only: starts a superset (AP-0k: discoverable even with one
   *  exercise; the screen routes it to the right picker). */
  onCreate?: () => void;
  /** The ☰ hamburger: the superset management menu. Falls back to onSplit. */
  onOpenMenu?: () => void;
  /** Editing a past session: creating and dissolving are forbidden (S+ and ☰
   *  greyed, a tap explains why). Member navigation stays live. */
  editLocked: boolean;
  /** The card's single balloon, for the lock explanations. */
  showTip: (message: string) => void;
  /** Hold-for-hint (#308), like every control on the card. */
  hold: HoldProps;
  hints: CardHints;
  t: (k: string, opts?: Record<string, unknown>) => string;
  theme: ReturnType<typeof useTheme>;
}) {
  // 4 on every side: 36 + 8 = 44, and when row 2 wraps the zones of two
  // lines 8 apart do not overlap (#308 review).
  const slop = { top: 4, bottom: 4, left: 4, right: 4 };

  if (members.length === 0) {
    if (!onCreate) return null;
    const lockMsg = t('mobile.timeline.createGroupLocked', {
      defaultValue: "Editing a past session: you can't create a superset.",
    });
    return (
      <IconButton
        name="link"
        locked={editLocked}
        onPress={editLocked ? () => showTip(lockMsg) : onCreate}
        // The reason rides the NAME when locked: RNW drops accessibilityHint,
        // and a lock that only dims is a colour. aria-disabled comes from
        // IconButton `locked`.
        accessibilityLabel={
          t('mobile.logger.addGroup', {
            defaultValue: 'Create a superset with another exercise',
          }) + (editLocked ? `. ${lockMsg}` : '')
        }
        {...hold(hints.superset)}
        accessibilityHint={editLocked ? lockMsg : hints.superset}
        hitSlop={slop}
        theme={theme}
      />
    );
  }

  const splitLockMsg = t('mobile.timeline.splitGroupLocked', {
    defaultValue: "Editing a past session: you can't dissolve a superset.",
  });
  const activePos = Math.max(
    0,
    members.findIndex((m) => rowIdOf(m.entry) === activeEntryId),
  );
  // Cycle forward, wrapping to the first member; null for a lone member.
  const cycleNext =
    members.length > 1 ? members[(activePos + 1) % members.length] : null;
  return (
    <>
      <PositionCue
        theme={theme}
        glyph="repeat"
        position={activePos + 1}
        total={members.length}
        hitSlop={slop}
        {...hold(hints.supersetCue)}
        accessibilityLabel={
          cycleNext
            ? t('mobile.superset.cueCycle', {
                defaultValue:
                  '{{group}}, exercise {{pos}} of {{total}}. Tap to go to {{next}}.',
                group: groupLabel,
                pos: activePos + 1,
                total: members.length,
                next: resolveName(
                  cycleNext.entry.exerciseId,
                  cycleNext.entry.exerciseName,
                ),
              })
            : t('mobile.superset.cueStatic', {
                defaultValue: '{{group}}, exercise {{pos}} of {{total}}',
                group: groupLabel,
                pos: activePos + 1,
                total: members.length,
              })
        }
        onPress={
          cycleNext ? () => onSelect(rowIdOf(cycleNext.entry)) : undefined
        }
      />
      <IconButton
        name="menu"
        locked={editLocked}
        onPress={
          editLocked ? () => showTip(splitLockMsg) : (onOpenMenu ?? onSplit)
        }
        accessibilityLabel={
          t('mobile.superset.menuOpen', {
            defaultValue: 'Superset options',
          }) + (editLocked ? `. ${splitLockMsg}` : '')
        }
        {...hold(hints.supersetMenu)}
        // Spoken lock reason: the ☰ stays an enabled button, tappable to
        // explain, not disabled.
        accessibilityHint={editLocked ? splitLockMsg : hints.supersetMenu}
        hitSlop={slop}
        theme={theme}
      />
    </>
  );
}
